import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import ky from 'ky';

import { setIsBackendExist, loadData, DbKeyType } from './dataBackend';
import { CardTemplateDbHashType } from './cardTemplateDbSlice';
import { BookDbHashType } from './bookDbSlice';
import { PaletteDbHashType } from './paletteDbSlice';

import { Hash, getCookie, setTokensToCookie } from '../general';
import { apiURL } from '../components/DigimagiProvider';

export type AuthUserType = { uid: string; } | 'noAuth' | null
type SnackbarState = { open: boolean; message: string; };
export const getLoginCheckData = () => {
  return { user_id: getCookie('user_id'), token: getCookie('token'), refresh_token: getCookie('refresh_token') };
}

export type WorkBackend = {
  authUser: AuthUserType;
  latest: boolean;
  currentWorkId: number | null;
  loading: boolean;
  errorMessage: string;
  okMessage: string;
  snackbar: SnackbarState;
}

const initialState: WorkBackend = {
  authUser: null,
  latest: false,
  currentWorkId: null,
  loading: false,
  errorMessage: "",
  okMessage: "",
  snackbar: { open: false, message: '' }
}

const workBackendSlice = createSlice({
  name: 'workBackend',
  initialState,
  reducers: {
    authLogin(state, action: PayloadAction<boolean>) {
      const userId = action.payload ? getCookie('user_id') : undefined;
      if (userId) {
        state.authUser = { uid: userId };
        setIsBackendExist(true);
      } else {
        state.authUser = 'noAuth';
        setIsBackendExist(false);
      }
    },
    setLatest(state, action: PayloadAction<boolean>) {
      state.latest = action.payload;
    },
    setCurrentWorkId(state, action: PayloadAction<number | null>) {
      state.currentWorkId = action.payload
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload
    },
    setMessage(state, action: PayloadAction<{error?: string, ok?: string}>) {
      state.okMessage = action.payload.ok ?? ""
      state.errorMessage = action.payload.error ?? ""
    },
    setSnackbar(state, action: PayloadAction<SnackbarState>) {
      state.snackbar = action.payload;
    },
    clear(_state, _action: PayloadAction<void>) {
      return {...initialState}
    }
  }
});

type AllDataHash = Hash<Hash<Object>>
let allDataToLoad: AllDataHash = {};
export const clearAllDataToLoad = () => {
  allDataToLoad = {};
}
export const dbGet = async () => {
  let hash: AllDataHash = {};

  const sendData = { user_id: getCookie('user_id') };
  const query = await ky.post(`${apiURL}load_all_db`, { json: sendData }).json() as any;
  type ResultDataType = { id: string; jdoc: string; }[]
  const data = query.data as Hash<ResultDataType>;
  Object.keys(data).forEach(key => {
    const sliceData: Hash<Object> = {};
    data[key].forEach(record => sliceData[record.id] = JSON.parse(record.jdoc))
    hash[key] = sliceData;
  })

  allDataToLoad = hash;
}
export const getSliceData = (key: DbKeyType): Hash<Object> => {
  return allDataToLoad[key];
}

let allDataToSave: AllDataHash = {};
export const clearAllDataToSave = () => {
  allDataToSave = {};
}
export const addDatasToSave = (key: DbKeyType, values: Hash<Object>) => {
  allDataToSave[key] = values;
}
export const addDataToSave = (key: DbKeyType, id: string, value: Object) => {
  if (!allDataToSave[key]) allDataToSave[key] = {};
  allDataToSave[key][id] = value;
}
const registerPurchases = async (hash: AllDataHash) => {
  const cardTemplates = hash['cardTemplates'] as CardTemplateDbHashType;
  const books = hash['books'] as BookDbHashType;
  const palettes = hash['palettes'] as PaletteDbHashType;

  const goodsList: [string, number][] = [];

  const getGoodList = (values: CardTemplateDbHashType | BookDbHashType | PaletteDbHashType) => {
    Object.keys(values).forEach(id => {
      try {
        const value = values[id];
        if (!value.goodsId || !value.version) return;
        const isOverlaped = goodsList.find(pair => pair[0] === value.goodsId && pair[1] === value.version);
        if (!isOverlaped) goodsList.push([value.goodsId, value.version]);
      } catch (err) {
        console.log(err);
      }
    })
  }
  getGoodList(cardTemplates);
  getGoodList(books);
  getGoodList(palettes);

  const loginCheckData = getLoginCheckData();
  const query = await ky.post(`${apiURL}register_purchases`, { json: { ...loginCheckData, list: goodsList } }).json() as any;
  const res = query.data;

  const newToken = res.newToken;
  if (newToken && typeof newToken === 'object') setTokensToCookie(newToken);
}
export const dbSave = async (isSignup: boolean, callback?: () => void) => {
  try {
    const sendData = { hash: allDataToSave };
    await ky.post(`${apiURL}save_all_db`, { json: sendData });
    if (isSignup) await registerPurchases(allDataToSave);
  } catch (err) {
    console.log(err);
  } finally {
    clearAllDataToSave();
    if (callback) callback();
  }
}

const getUrl = (key: string) => {
  const name = key.substring(0, key.length);
  const initial = key.substring(0, 1).toUpperCase();
  const letters = key.substring(1);
  const url = name + 'Db/load' + initial + letters;
  return url;
}
export const loadDbModel = (key: DbKeyType) => {
  const url = getUrl(key);
  return createAsyncThunk(url, () => loadData(key));
}
export const loadDb = (key: DbKeyType) => {
  const url = getUrl(key);
  return createAsyncThunk(url, () => loadData(key))();
}

export const { authLogin, setLatest, setCurrentWorkId, setLoading, setMessage, setSnackbar, clear } = workBackendSlice.actions;
export default workBackendSlice.reducer;
